import { useMemo } from 'react';
import {
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    DialogActions,
    Grid,
    Checkbox,
    FormControlLabel,
    Box,
    Typography
} from '@material-ui/core';
import moment from 'moment';
import { DatePicker } from '@material-ui/pickers';
import { Form, Formik } from 'formik';
import GuestFieldInput, { validateGuestFields } from '../GuestFieldInput';
import AutocompleteSelect from '../atom/AutocompleteSelect';
import { sourceAutocomplete } from '../../autocomplete';
import { PlusIcon, DeleteIcon } from '../../icons';
import config from '../../config';
import { formatPhone, validatePhone } from '../../helpers/phoneUtils';

const defaultCustomer = {
    id: undefined,
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    termsAndConditions: true,
    guest_field_info: [],
    additional_emails: [],
    notification: ['email'],
    source_id: null
};

function checkGuestFields(values, guest_fields) {
    const guestFieldCleaned = [];
    for (let i = 0; i < guest_fields.length; i++) {
        const field = guest_fields[i];
        const fieldInRequest = values.guest_field_info.find(e => e.id === field.id);
        if (
            (!fieldInRequest ||
                typeof fieldInRequest.value === 'undefined' ||
                fieldInRequest.value === '') &&
            field.required === true
        ) {
            return false;
        }
        if (!fieldInRequest) {
            continue;
        }
        guestFieldCleaned.push({
            id: field.id,
            name: field.name,
            value: fieldInRequest.value
        });
    }
    return true;
}
function validateGuestForm(values, guest_fields) {
    const errors = {};
    if (!values.firstname) {
        errors.firstname = 'First name is required.';
    }
    if (!values.lastname) {
        errors.lastname = 'Last name is required.';
    }
    const phoneError = validatePhone(values.phone);
    if (phoneError) {
        errors.phone = phoneError;
    }
    // validate guest field phone/email formats
    const guestFieldErrors = validateGuestFields(values.guest_field_info, guest_fields);
    Object.assign(errors, guestFieldErrors);
    return errors;
}

function CreateOrUpdateGuestModal({
    isAgent,
    customer,
    id,
    handleSubmit,
    showModal,
    guest_fields,
    MODAL_TYPE
}) {
    const handleEventValue = (values, setFieldValue, field, value) => {
        const guestInfo = values.guest_field_info || [];
        const index = guestInfo.findIndex(e => e.id === field.id);
        if (index === -1) {
            guestInfo.push({ id: field.id, name: field.name, value });
        } else {
            guestInfo[index] = { ...guestInfo[index], value };
        }
        setFieldValue('guest_field_info', guestInfo);
    };

    const handleNotificationEventValue = (values, setFieldValue, field, value) => {
        let notifications = values.notification ? [...values.notification] : [];
        if (!notifications.includes(field) && value) {
            notifications.push(field);
        }
        if (!value) {
            notifications = notifications.filter(e => e !== field);
        }
        setFieldValue('notification', notifications);
    };

    const onSubmit = async (values, { setSubmitting }) => {
        try {
            const emptyTextfield = (values.additional_emails ?? [])
                .map(field => (field ?? '').trim())
                .filter(field => field !== '');

            const data = {
                ...values,
                additional_emails: emptyTextfield
            };

            if (data.termsAndConditions || customer) {
                await handleSubmit(data);
            }
        } catch (err) {
            console.error(err);
        }
        setSubmitting(false);
    };

    const initialFormValues = useMemo(() => {
        const values = JSON.parse(JSON.stringify(customer || { ...defaultCustomer, id }));
        if (values.phone) {
            values.phone = formatPhone(values.phone);
        }
        return values;
    }, [customer, id]);

    return (
        <Dialog
            aria-labelledby="guest-new-modal"
            open
            onClose={() => showModal(MODAL_TYPE.NONE)}
            maxWidth="sm"
            disableEnforceFocus
        >
            <Formik
                onSubmit={onSubmit}
                initialValues={initialFormValues}
                validate={vals => validateGuestForm(vals, guest_fields)}
            >
                {({ isSubmitting, values, setFieldValue, errors, touched, setFieldTouched }) => (
                    <Form>
                        <DialogTitle>{values.id ? 'Edit guest data' : 'Add new guest'}</DialogTitle>
                        <DialogContent>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="First name"
                                        required
                                        type="text"
                                        value={values.firstname}
                                        onChange={ev => setFieldValue('firstname', ev.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Last name"
                                        required
                                        type="text"
                                        value={values.lastname}
                                        onChange={ev => setFieldValue('lastname', ev.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Email"
                                        type="email"
                                        value={values.email}
                                        onChange={ev => setFieldValue('email', ev.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        fullWidth
                                        label="Phone"
                                        required
                                        type="tel"
                                        value={values.phone}
                                        onChange={ev => setFieldValue('phone', ev.target.value)}
                                        onBlur={() => {
                                            setFieldTouched('phone', true);
                                            if (values.phone) {
                                                const formatted = formatPhone(values.phone);
                                                if (formatted !== values.phone) {
                                                    setFieldValue('phone', formatted);
                                                }
                                            }
                                        }}
                                        error={touched.phone && !!errors.phone}
                                        helperText={touched.phone && errors.phone}
                                    />
                                </Grid>
                                {/* Guest fields on user table */}
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Arrival date"
                                        value={values.arrival_date || null}
                                        onChange={newDate => {
                                            if (
                                                values.departure_date !== null &&
                                                values.departure_date !== undefined &&
                                                moment(newDate).isAfter(
                                                    moment(values.departure_date)
                                                )
                                            ) {
                                                // TODO: needs some visual clue why it did not change
                                                return;
                                            }
                                            setFieldValue(
                                                'arrival_date',
                                                moment(newDate).format('YYYY-MM-DD')
                                            );
                                        }}
                                        renderInput={props => (
                                            <TextField {...props} helperText="" />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <DatePicker
                                        label="Departure date"
                                        value={values.departure_date || null}
                                        onChange={newDate => {
                                            if (
                                                values.arrival_date !== null &&
                                                values.arrival_date !== undefined &&
                                                moment(newDate).isBefore(
                                                    moment(values.arrival_date)
                                                )
                                            ) {
                                                // TODO: needs some visual clue why it did not change
                                                return;
                                            }
                                            setFieldValue(
                                                'departure_date',
                                                moment(newDate).format('YYYY-MM-DD')
                                            );
                                        }}
                                        renderInput={props => (
                                            <TextField {...props} helperText="" />
                                        )}
                                    />
                                </Grid>
                                {isAgent && (
                                    <Grid item xs={12}>
                                        <AutocompleteSelect
                                            initCall={sourceAutocomplete.init}
                                            autocompleteCall={sourceAutocomplete.search}
                                            value={values.source_id}
                                            setValue={value => setFieldValue('source_id', value)}
                                        >
                                            <TextField label="Source" />
                                        </AutocompleteSelect>
                                    </Grid>
                                )}
                                {isAgent && (
                                    <Grid item xs={12}>
                                        <TextField
                                            placeholder="Internal note for agents"
                                            multiline
                                            rows={4}
                                            rowsMax={8}
                                            fullWidth
                                            variant="outlined"
                                            value={values.internal_notes || ''}
                                            onChange={ev =>
                                                setFieldValue('internal_notes', ev.target.value)
                                            }
                                        />
                                    </Grid>
                                )}
                                {/* Guest fields in the same order as in clients config */}
                                {guest_fields
                                    .filter(
                                        field =>
                                            (field?.agentOnly === true && isAgent) ||
                                            field?.agentOnly !== true
                                    )
                                    .map(field => (
                                        <Grid
                                            item
                                            xs={12}
                                            sm={field.type === 'hotel' ? 12 : 6}
                                            key={field.id}
                                        >
                                            <GuestFieldInput
                                                field={field}
                                                values={values}
                                                setFieldValue={setFieldValue}
                                                handleEventValue={handleEventValue}
                                            />
                                        </Grid>
                                    ))}
                            </Grid>
                            <Grid container spacing={1} style={{ marginBottom: '15px' }}>
                                {(values.additional_emails.length > 0
                                    ? values.additional_emails
                                    : ['']
                                ).map((email, index) => (
                                    <Grid
                                        container
                                        item
                                        spacing={1}
                                        alignItems="center"
                                        key={index}
                                    >
                                        <Grid item xs={10} sm={5}>
                                            <TextField
                                                fullWidth
                                                label="Additional email address"
                                                type="email"
                                                value={email}
                                                onChange={ev => {
                                                    const newValue = [...values.additional_emails];
                                                    newValue[index] = ev.target.value;
                                                    setFieldValue('additional_emails', newValue);
                                                }}
                                            />
                                        </Grid>
                                        {values.additional_emails.length > 0 && (
                                            <Grid item>
                                                <DeleteIcon
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        const newValue = values.additional_emails.filter(
                                                            (_, i) => i !== index
                                                        );
                                                        setFieldValue(
                                                            'additional_emails',
                                                            newValue
                                                        );
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                        {index === values.additional_emails.length - 1 && (
                                            <Grid item>
                                                <PlusIcon
                                                    style={{ cursor: 'pointer' }}
                                                    onClick={() => {
                                                        setFieldValue('additional_emails', [
                                                            ...values.additional_emails,
                                                            ''
                                                        ]);
                                                    }}
                                                />
                                            </Grid>
                                        )}
                                    </Grid>
                                ))}
                            </Grid>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <h4>Notifications</h4>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={
                                                    values.notification?.includes('email') || false
                                                }
                                                onChange={ev =>
                                                    handleNotificationEventValue(
                                                        values,
                                                        setFieldValue,
                                                        'email',
                                                        !!ev.target.checked
                                                    )
                                                }
                                            />
                                        }
                                        label="email"
                                    />
                                    <Box>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        values.notification?.includes('sms') ||
                                                        false
                                                    }
                                                    onChange={ev =>
                                                        handleNotificationEventValue(
                                                            values,
                                                            setFieldValue,
                                                            'sms',
                                                            !!ev.target.checked
                                                        )
                                                    }
                                                />
                                            }
                                            label="sms"
                                        />
                                        <Box ml={1}>
                                            <Typography variant="caption" color="text.secondary">
                                                By checking this box, you agree to receive SMS from{' '}
                                                <b>Activiter</b>. Msg & data rates may apply. Msg
                                                frequency varies. Reply <b>STOP</b> to opt out.{' '}
                                                <a
                                                    href={`${config.assetsUrl}/termsandconditions.pdf`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                >
                                                    Terms & Conditions
                                                </a>
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                variant="contained"
                                onClick={() => {
                                    showModal(MODAL_TYPE.NONE);
                                }}
                            >
                                Close
                            </Button>
                            <Button
                                disabled={
                                    !(
                                        values.firstname &&
                                        values.lastname &&
                                        values.phone &&
                                        !errors.phone &&
                                        checkGuestFields(values, guest_fields) &&
                                        (values.termsAndConditions || customer)
                                    ) || isSubmitting
                                }
                                variant="contained"
                                color="primary"
                                type="submit"
                            >
                                {customer
                                    ? isAgent
                                        ? 'Update guest info'
                                        : 'Change data'
                                    : 'Create and Login as'}
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}
export default CreateOrUpdateGuestModal;
